import React, { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  BarChart3, PieChart, TrendingUp, Monitor, Globe, Tag,
  Clock, Shield, Mail, RefreshCw, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';

const TrackingStatistics = () => {
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const primaryColor = settings?.primaryColor || '#00b30c';

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTrackingStatistics();
      setStats(data);
    } catch (err) {
      console.error('Error loading tracking stats:', err);
      setError('Error al cargar estadísticas');
      toast.error('Error al cargar estadísticas de tracking');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
    };
    
    return (
      <div className={`bg-white rounded-xl p-5 border ${colors[color].split(' ')[2]}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg ${colors[color].split(' ')[0]}`}>
            <Icon className={`w-5 h-5 ${colors[color].split(' ')[1]}`} />
          </div>
        </div>
      </div>
    );
  };

  const BarChartSimple = ({ data, title, color = '#00b30c' }) => {
    if (!data || data.length === 0) return null;
    const maxCount = Math.max(...data.map(d => d.count));
    
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.slice(0, 8).map((item, idx) => (
            <div key={idx} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 truncate max-w-[60%]">{item.name || 'Sin datos'}</span>
                <span className="font-medium text-slate-900">{item.count}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${(item.count / maxCount) * 100}%`,
                    backgroundColor: color
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-700 font-medium">{error}</p>
          <button 
            onClick={loadStats}
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const hasData = stats && (
    (stats.sources && stats.sources.length > 0) ||
    (stats.devices && stats.devices.length > 0) ||
    (stats.mediums && stats.mediums.length > 0)
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Estadísticas de Tracking</h1>
          <p className="text-slate-500 mt-1">
            Análisis de datos de atribución y comportamiento de leads
          </p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {!hasData ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
          <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Sin datos de tracking aún</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Los datos de tracking se capturan cuando los leads llenan formularios con el script de tracking instalado.
            Una vez que tengas leads con datos de tracking, aquí verás las estadísticas.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Tiempo promedio en página"
              value={stats.avgTimeOnPage ? `${stats.avgTimeOnPage}s` : 'N/A'}
              subtitle="Segundos"
              icon={Clock}
              color="blue"
            />
            <StatCard
              title="Tiempo de llenado formulario"
              value={stats.avgFormFillDuration ? `${stats.avgFormFillDuration}s` : 'N/A'}
              subtitle="Promedio"
              icon={TrendingUp}
              color="green"
            />
            <StatCard
              title="Detección de Bots"
              value={stats.botDetection?.bots || 0}
              subtitle={`${stats.botDetection?.percentage || 0}% del total`}
              icon={Shield}
              color="orange"
            />
            <StatCard
              title="Fuentes únicas"
              value={stats.sources?.length || 0}
              subtitle="utm_source diferentes"
              icon={Tag}
              color="purple"
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarChartSimple
              data={stats.sources}
              title="Leads por Fuente (utm_source)"
              color="#3b82f6"
            />
            <BarChartSimple
              data={stats.mediums}
              title="Leads por Medio (utm_medium)"
              color="#8b5cf6"
            />
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BarChartSimple
              data={stats.devices}
              title="Por Dispositivo"
              color="#10b981"
            />
            <BarChartSimple
              data={stats.emailTypes}
              title="Tipo de Email"
              color="#f59e0b"
            />
            <BarChartSimple
              data={stats.referrers}
              title="URLs de Referencia"
              color="#ec4899"
            />
          </div>

          {/* Campaigns */}
          {stats.campaigns && stats.campaigns.length > 0 && (
            <div className="grid grid-cols-1 gap-6">
              <BarChartSimple
                data={stats.campaigns}
                title="Leads por Campaña (utm_campaign)"
                color={primaryColor}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrackingStatistics;
